import type { AdminChatThread } from "../types";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { AdminChatThreadHeader } from "./AdminChatThreadHeader";
import { AdminChatMessageList } from "./AdminChatMessageList";
import { useEffect } from "react";
import { asyncInterval } from "@/common/utils/asyncInterval";
import { useTRPC } from "@/trpc";

interface ThreadViewProps {
  thread: AdminChatThread;
  onBack: () => void;
  readOnly?: boolean;
}

// Loading state component
const LoadingState = () => (
  <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center bg-slate-50 p-4">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
    <p className="mt-2 text-slate-600">Loading messages...</p>
  </div>
);

// Error state component
const ErrorState = () => (
  <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center bg-slate-50 p-4 text-red-600">
    <AlertCircle className="h-8 w-8" />
    <p className="mt-2">Failed to load messages</p>
    <Button
      variant="outline"
      className="mt-4"
      onClick={() => window.location.reload()}
    >
      Retry
    </Button>
  </div>
);

// Main thread view component
export const AdminChatThreadView: React.FC<ThreadViewProps> = ({
  thread,
  onBack,
}) => {
  const trpc = useTRPC();
  const {
    data: messages,
    isLoading,
    error,
  } = useQuery({
    ...trpc.chat.adminGetChatThreadMessages.queryOptions(
      { threadId: thread.id },
      {
        staleTime: 0,
        refetchInterval: () => 5_000 + Math.random() * 5_000,
      },
    ),
  });

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        cleanup = asyncInterval({
          fn: async () => {
            // TODO: Implement heartbeat
          },
          interval: 8_000,
          jitter: 4_000,
        });
      } else {
        cleanup?.();
        cleanup = undefined;
      }
    };

    // Start interval if initially visible
    handleVisibilityChange();

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cleanup?.();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [thread.id]);

  if (isLoading) {
    return <LoadingState />;
  }
  if (error) {
    return <ErrorState />;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <AdminChatThreadHeader thread={thread} onBack={onBack} />
      <AdminChatMessageList
        messages={messages}
        threadId={thread.id}
        user={{ is_admin: true }}
      />
    </div>
  );
};
